/**
 * DER PAPIERKORB ALLER AUFTRÄGE (.351).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (12.10.2026)
 *   „haben wir die möglichkeit für mich als admin einen papierkorb
 *    einzubauen, das falls dateien falsch gelöscht worden ich sie dort
 *    wiederholen kann? der papierkorb kann nach 10 tagen geleert werden."
 *
 * Entfernt wurde schon immer nur der Bezug; jeder Auftrag hat seinen
 * Papierkorb unten im Reiter Dateien. Hier stehen alle an einer Stelle, mit
 * Auftrag, Ordner, wer und wann, und wann der nächtliche Lauf sie endgültig
 * entfernt (papierkorb.tage).
 */
import { useEffect, useState } from 'react';
import { api, zeitpunkt, datum, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Suchfeld } from '../teile/Schluesselsuche.tsx';

const anschrift = (z: any) => z.strasse
  ? `${z.strasse} ${z.hausnummer ?? ''}${z.hausnummer_zusatz ?? ''}`.trim() + (z.ort_text ? `, ${z.plz ?? ''} ${z.ort_text}`.replace(/ ,/, ',') : '')
  : '';
const groesse = (b: number | null) => b == null ? '' : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

export default function Papierkorb({ oeffne }: { ich: Anmelder; oeffne: (auftragId: string, reiter?: string) => void }) {
  const [d, setD] = useState<{ zeilen: any[]; tage: number } | null>(null);
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = async () => {
    try { setD(await api.papierkorb(suche.trim() || undefined)); }
    catch (e) { setFehler(ausFehler(e)); }
  };
  useFrisch(laden);
  useEffect(() => { const t = setTimeout(() => { void laden(); }, suche ? 350 : 0); return () => clearTimeout(t); }, [suche]); // eslint-disable-line react-hooks/exhaustive-deps

  const zurueck = async (z: any) => {
    setLaeuft(z.id); setFehler(null); setHinweis(null);
    try {
      await api.dateiZurueckholen(z.id);
      setHinweis(`${z.dateiname} liegt wieder in ${z.ordner}${anschrift(z) ? ` (${anschrift(z)})` : ''}.`);
      await laden();
    } catch (e) { setFehler(ausFehler(e)); }
    finally { setLaeuft(null); }
  };

  const tage = d?.tage ?? 0;
  return (
    <>
      <Kopf titel="Papierkorb" weg="Entfernte Dateien aller Aufträge, zum Zurückholen" />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}
        <p className="klein leise" style={{ marginTop: 0 }}>
          {tage > 0
            ? `Nach ${tage} Tagen entfernt der nächtliche Lauf eine Datei endgültig. Bis dahin lässt sie sich hier zurückholen.`
            : 'Hier wird nichts automatisch gelöscht (papierkorb.tage steht auf 0).'}
        </p>
        <Karte titel={`Entfernte Dateien${d ? ` (${d.zeilen.length})` : ''}`}
               rechts={<Suchfeld wert={suche} setze={setSuche} mehr="Dateiname, Ordner" breit={300} />}>
          {!d ? <p className="klein leise">Wird geladen.</p>
            : !d.zeilen.length ? <Leer text={suche ? 'Nichts passt zur Suche.' : 'Der Papierkorb ist leer.'} />
            : (
              <div className="tabellenrahmen">
                <table className="liste">
                  <thead><tr><th>Datei</th><th>Auftrag</th><th>Entfernt</th><th>Endgültig weg</th><th></th></tr></thead>
                  <tbody>
                    {d.zeilen.map((z) => (
                      <tr key={z.id} style={{ cursor: 'default' }}>
                        <td>
                          <b>{z.dateiname}</b>
                          <div className="klein leise">{z.ordner}{z.groesse ? `, ${groesse(Number(z.groesse))}` : ''}</div>
                        </td>
                        <td>
                          {z.auftrag_id
                            ? <button className="alslink" onClick={() => oeffne(z.auftrag_id, 'dateien')}>{anschrift(z) || 'Auftrag'}</button>
                            : <span className="leise">ohne Auftrag</span>}
                          <div className="klein leise">{z.kls_id ? `KLS ${z.kls_id}` : ''}{z.sm_nummer ? `, SM ${z.sm_nummer}` : ''}</div>
                        </td>
                        <td className="klein">
                          {zeitpunkt(z.entfernt_am)}
                          {z.entfernt_von_name && <div className="leise">von {z.entfernt_von_name}</div>}
                          {z.entfernt_grund && <div className="leise">{z.entfernt_grund}</div>}
                        </td>
                        <td className="klein">{z.endgueltig_am ? datum(z.endgueltig_am) : 'nie'}</td>
                        <td>
                          <button className="knopf klein" disabled={!!laeuft} onClick={() => zurueck(z)}>
                            {laeuft === z.id ? '…' : 'Zurückholen'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Karte>
      </div>
    </>
  );
}
